import { useState } from "react";
import axios from "axios";
import { BadgeCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-11 bg-black/40 border-white/25 text-[color:var(--axt-text)] placeholder:text-white/35 rounded-sm font-mono-axt text-sm focus-visible:ring-1 focus-visible:ring-white/70 focus-visible:ring-offset-0 focus-visible:border-white/70 transition-[border-color,box-shadow] duration-200";
const errorCls = "border-[hsl(0_62%_52%)]/60";
const labelCls = "font-mono-axt text-[10px] tracking-[0.16em] uppercase text-[color:var(--axt-text-dim)]";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Waitlist capture. `compact` = email-only row (dialog); otherwise email + optional name/firm.
 * `source` is recorded with the signup so we know which CTA converted.
 */
export const WaitlistForm = ({ source = "landing", compact = false, idPrefix = "wl" }) => {
  const [values, setValues] = useState({ email: "", name: "", firm: "", website: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (k === "email" && error) setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const email = values.email.trim();
    if (!EMAIL_RE.test(email)) {
      setError("Enter a valid work email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        email,
        name: values.name.trim() || null,
        firm: values.firm.trim() || null,
        source,
        website: values.website || null,
      });
      setDone(data);
      toast.success(data.already_joined ? "Already on the list" : "You're on the list", {
        description: data.already_joined ? "We have your email — we'll be in touch." : `Position #${data.position}. A senior fund accountant will reach out.`,
        className: "axt-toast",
        id: "waitlist-success-toast",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : detail || "Something went wrong. Please try again.";
      toast.error("Couldn't add you to the waitlist", { description: msg, className: "axt-toast axt-toast-error", id: "waitlist-error-toast" });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="waitlist-success" className="relative flex items-start gap-5 py-2">
        <div className="relative w-14 h-14 shrink-0 rounded-sm flex items-center justify-center axt-metal">
          <span className="absolute inset-0 rounded-sm axt-breathe" style={{ boxShadow: "0 0 36px var(--axt-glow-strong)" }} aria-hidden="true" />
          <BadgeCheck className="relative w-7 h-7 text-[color:var(--axt-accent)]" strokeWidth={1.6} />
        </div>
        <div>
          <p className="axt-eyebrow mb-2">{done.already_joined ? "Already listed" : "Sealed"}</p>
          <h3 className="font-display text-2xl sm:text-3xl leading-tight text-[color:var(--axt-text)]">
            {done.already_joined ? "You're already on the list." : "You're on the list."}
          </h3>
          <p className="mt-2 text-sm text-[color:var(--axt-text-dim)] max-w-md">
            A senior fund accountant — not a sales bot — will reach out when your slot opens.
          </p>
          <p className="mt-3 font-mono-axt text-[11px] tracking-[0.14em] uppercase text-[color:var(--axt-text-faint)]" data-testid="waitlist-position">
            Position #{String(done.position).padStart(3, "0")} · {done.email}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="waitlist-form" onSubmit={onSubmit} noValidate className="relative grid gap-4">
      {!compact && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor={`${idPrefix}-name`} className={labelCls}>
              Name <span className="text-[color:var(--axt-text-faint)]">(optional)</span>
            </Label>
            <Input id={`${idPrefix}-name`} data-testid="waitlist-name-input" value={values.name} onChange={set("name")} placeholder="Alexandra Reyes" autoComplete="name" className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <Label htmlFor={`${idPrefix}-firm`} className={labelCls}>
              Firm <span className="text-[color:var(--axt-text-faint)]">(optional)</span>
            </Label>
            <Input id={`${idPrefix}-firm`} data-testid="waitlist-firm-input" value={values.firm} onChange={set("firm")} placeholder="Meridian Capital Partners" autoComplete="organization" className={`mt-2 ${inputCls}`} />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor={`${idPrefix}-email`} className={labelCls}>
          Work email
        </Label>
        <div className={`mt-2 flex flex-col ${compact ? "sm:flex-row" : ""} gap-3`}>
          <Input
            id={`${idPrefix}-email`}
            type="email"
            data-testid="waitlist-email-input"
            value={values.email}
            onChange={set("email")}
            placeholder="you@yourfund.com"
            autoComplete="email"
            aria-invalid={!!error}
            aria-describedby={error ? `${idPrefix}-email-error` : undefined}
            className={`${inputCls} ${error ? errorCls : ""} flex-1`}
          />
          <Button type="submit" data-testid="waitlist-submit-button" disabled={loading} className="axt-btn-primary h-11 px-6 shrink-0 disabled:opacity-70 disabled:cursor-not-allowed focus-visible:ring-1 focus-visible:ring-white/70">
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Joining…
              </span>
            ) : (
              "Join the waitlist"
            )}
          </Button>
        </div>
        {error && (
          <p id={`${idPrefix}-email-error`} data-testid="waitlist-email-error" role="alert" className="mt-1.5 text-xs text-[hsl(0_62%_62%)]">
            {error}
          </p>
        )}
      </div>

      {/* honeypot */}
      <div className="absolute -left-[9999px] top-0 w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor={`${idPrefix}-website`}>Website</label>
        <input id={`${idPrefix}-website`} tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} />
      </div>

      <p className="text-xs text-[color:var(--axt-text-faint)]">No spam. One email when your slot opens, from a senior fund accountant.</p>
    </form>
  );
};
